import type { QueryOptions} from '@tanstack/vue-query';
import { queryOptions } from '@tanstack/vue-query';
import { useLoginStore } from '@/login/stores/useLoginStore';
import type { CurrencyConversionStatus, MissingEmploymentsKeyValues, ReviewBudgetStatus } from '@sysarb/sysarb-types-v2/services/v1/pay-management';
import { ResgateManager, ResErr } from '@/newApi/services/ResgateManager';
import { RestManager } from '@/newApi/services/rest/RestManager';
import { getApiHost } from '@/appSettings';

const payManagementService = new RestManager(`https://${getApiHost()}/pay-management-service`);

type SalaryReviewOptions = {
    queries: {
        getEmploymentKeyValues: (reviewId: string | null | undefined) => QueryOptions<MissingEmploymentsKeyValues | null, Error>,
        getBudgetStatus: (reviewId: string | null | undefined) => QueryOptions<ReviewBudgetStatus | null, Error>,
        getCurrencyConversionStatus: (reviewId: string | null | undefined) => QueryOptions<CurrencyConversionStatus | null, Error>,
    },
};

export const salaryReview: SalaryReviewOptions = {
    queries: {
        getEmploymentKeyValues: (reviewId: string | null | undefined): QueryOptions<MissingEmploymentsKeyValues | null, Error> => {
            const { instanceTopic } = useLoginStore();
            const topic = `v1.pay-management.subdomain.${instanceTopic}.review.${reviewId}.employmenttotals`;
            const subscribe = true;
            return queryOptions<MissingEmploymentsKeyValues | null, Error>({
                meta: {
                    subscribe,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['employmentTotals', topic],
                queryFn: async (): Promise<MissingEmploymentsKeyValues | null> => {
                    return ResgateManager.model<MissingEmploymentsKeyValues>(topic).then(res => {
                        if (res instanceof ResErr) {
                            throw new Error(res.message);
                        }
                        return res;
                    });
                },
                enabled: () => reviewId != null,
            });
        },
        getBudgetStatus: (reviewId: string | null | undefined): QueryOptions<ReviewBudgetStatus | null, Error> => {
            const { instanceTopic } = useLoginStore();
            const topic = `v1.pay-management.subdomain.${instanceTopic}.budget.${reviewId}.budgetstatus`;
            const subscribe = true;
            return queryOptions<ReviewBudgetStatus | null, Error>({
                meta: {
                    subscribe,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['budgetStatus', topic],
                queryFn: async (): Promise<ReviewBudgetStatus | null> => {
                    return ResgateManager.model<ReviewBudgetStatus>(topic).then(res => {
                        if (res instanceof ResErr) {
                            throw new Error(res.message);
                        }
                        return res;
                    });
                },
                enabled: () => reviewId != null,
            });
        },
        getCurrencyConversionStatus: (reviewId: string | null | undefined): QueryOptions<CurrencyConversionStatus | null, Error> => {
            const { instance } = useLoginStore();
            const url = `/v1/${instance}/pay-management/review/${reviewId}/currencyconversionstatus`;
            return queryOptions<CurrencyConversionStatus | null, Error>({
                queryKey: ['currencyConversionStatus', url],
                queryFn: async (): Promise<CurrencyConversionStatus | null> => {
                    try {
                        return await payManagementService.get<CurrencyConversionStatus | null>(url);
                    } catch (e: unknown) {
                        console.error('Error fetching currency conversion status:', e);
                        return null;
                    }
                },
                enabled: () => reviewId != null,
            });
        }
    },
};
